import React, {FC, useEffect, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {SaveOutlined} from "@ant-design/icons";
import {Alert, App, Button, Col, Row, Space, Switch, Typography} from "antd";
import {Panel} from "../../../Components/ui";
import {getUserFacingErrorMessage} from "../../../userFacingErrorUtils";
import {
    createDefaultPaymentMethodState,
    eventPaymentMethodCategories,
    EventPaymentMethodState,
    eventPaymentMethodsToState,
    EventPaymentMethodToggleKey,
    hasAnyPaymentMethodEnabled
} from "./eventConfigPayloads";
import {
    EventPaymentMethodCategory,
    EventPaymentMethodConfig
} from "./eventConfigQueries";
import {EventConfigScope} from "./useEventConfigScope";
import {
    useCreateEventPaymentMethodForConfigMutation,
    useEventPaymentMethodsQuery,
    useUpdateEventPaymentMethodForConfigMutation
} from "../../../GraphQL/generated/graphql";

const {Text} = Typography;

export const EventPaymentMethodsPanel: FC<{scope: EventConfigScope}> = ({scope: {eventNumericId, errorLabels}}) => {
    const {message} = App.useApp();
    const {t} = useTranslation();
    const [paymentMethodState, setPaymentMethodState] = useState<EventPaymentMethodState>(createDefaultPaymentMethodState);
    const eventPaymentMethodsQuery = useEventPaymentMethodsQuery({
        variables: eventNumericId ? {eventId: eventNumericId} : undefined,
        skip: !eventNumericId,
        fetchPolicy: "cache-and-network"
    });
    const [createEventPaymentMethod, {loading: creatingEventPaymentMethod}] = useCreateEventPaymentMethodForConfigMutation();
    const [updateEventPaymentMethod, {loading: updatingEventPaymentMethod}] = useUpdateEventPaymentMethodForConfigMutation();

    const existingPaymentMethodsByCategory = useMemo(() => {
        const map = new Map<EventPaymentMethodCategory, EventPaymentMethodConfig>();

        (eventPaymentMethodsQuery.data?.eventPaymentMethods?.items ?? []).forEach(item => {
            if (item && eventPaymentMethodCategories.some(({category}) => category === item.category)) {
                map.set(item.category, item);
            }
        });

        return map;
    }, [eventPaymentMethodsQuery.data?.eventPaymentMethods?.items]);
    const missingPaymentMethodCategories = useMemo(
        () => eventPaymentMethodCategories.filter(({category}) => !existingPaymentMethodsByCategory.has(category)),
        [existingPaymentMethodsByCategory]);

    useEffect(() => {
        setPaymentMethodState(
            eventPaymentMethodsToState(eventPaymentMethodsQuery.data?.eventPaymentMethods?.items ?? [])
        );
    }, [eventPaymentMethodsQuery.data?.eventPaymentMethods?.items]);

    const paymentMethodToggles: Array<{key: EventPaymentMethodToggleKey; ariaSuffix: string; label: string}> = [
        {key: "bankTransferAllowed", ariaSuffix: "Bank transfer", label: t("payment-method-bank-transfer", {defaultValue: "Bank transfer"})},
        {key: "cardAllowed", ariaSuffix: "Card", label: t("payment-method-card", {defaultValue: "Card (Stripe)"})},
        {key: "posAllowed", ariaSuffix: "POS", label: t("payment-method-pos", {defaultValue: "POS"})}
    ];
    const savingEventPaymentMethods = creatingEventPaymentMethod || updatingEventPaymentMethod;
    const hasUnsavedPaymentMethodDefaults = !!eventNumericId &&
        !eventPaymentMethodsQuery.loading &&
        missingPaymentMethodCategories.length > 0;
    const invalidPaymentMethodCategory = eventPaymentMethodCategories.find(
        ({category}) => !hasAnyPaymentMethodEnabled(paymentMethodState[category])
    );

    const handlePaymentMethodToggle = (
        category: EventPaymentMethodCategory,
        key: EventPaymentMethodToggleKey,
        checked: boolean
    ) => {
        setPaymentMethodState(previous => ({
            ...previous,
            [category]: {
                ...previous[category],
                [key]: checked
            }
        }));
    };

    const handleSavePaymentMethods = async () => {
        if (!eventNumericId) {
            return;
        }

        if (invalidPaymentMethodCategory) {
            message.error(t("event-payment-method-required", {
                defaultValue: "Enable at least one payment method for each category."
            }));
            return;
        }

        try {
            await Promise.all(eventPaymentMethodCategories.map(async ({category}) => {
                const existing = existingPaymentMethodsByCategory.get(category);
                const input = {
                    eventId: eventNumericId,
                    category,
                    ...paymentMethodState[category]
                };

                if (existing?.id) {
                    await updateEventPaymentMethod({variables: {input: {...input, id: existing.id}}});
                    return;
                }

                await createEventPaymentMethod({variables: {input}});
            }));

            message.success(t("event-payment-methods-saved", {
                defaultValue: "Payment methods saved"
            }));
            await eventPaymentMethodsQuery.refetch();
        } catch (error: any) {
            message.error(getUserFacingErrorMessage(error, t, errorLabels));
        }
    };

    return (
        <Panel
            title={t("payment-methods", {defaultValue: "Payment methods"})}
            extra={(
                <Button
                    type="primary"
                    icon={<SaveOutlined/>}
                    disabled={!eventNumericId || eventPaymentMethodsQuery.loading || !!invalidPaymentMethodCategory}
                    loading={savingEventPaymentMethods}
                    onClick={handleSavePaymentMethods}
                >
                    {t("save-payment-methods", {defaultValue: "Save payment methods"})}
                </Button>
            )}
        >
            <div className="ati-stack ati-stack--compact">
                {eventPaymentMethodsQuery.error && (
                    <Alert
                        type="error"
                        showIcon
                        message={t("error-loading-items", {defaultValue: "Error loading items"})}
                        description={getUserFacingErrorMessage(eventPaymentMethodsQuery.error, t, errorLabels)}
                    />
                )}
                {hasUnsavedPaymentMethodDefaults && (
                    <Alert
                        type="info"
                        showIcon
                        message={t("event-payment-methods-unsaved-defaults-title", {
                            defaultValue: "Payment method defaults are not saved yet"
                        })}
                        description={t("event-payment-methods-unsaved-defaults-description", {
                            defaultValue: "The switches below show the recommended bank transfer/card defaults. Missing categories are not available on the public form until you save payment methods."
                        })}
                    />
                )}
                {invalidPaymentMethodCategory && (
                    <Alert
                        type="warning"
                        showIcon
                        message={t("event-payment-method-required", {
                            defaultValue: "Enable at least one payment method for each category."
                        })}
                    />
                )}
                <Row gutter={[16, 16]}>
                    {eventPaymentMethodCategories.map(({category, title, description}) => (
                        <Col xs={24} md={12} key={category}>
                            <div className="ati-field-tile">
                                <div className="ati-stack ati-stack--compact">
                                    <div>
                                        <Text strong>{t(`payment-method-category-${category.toLowerCase()}`, {defaultValue: title})}</Text>
                                        <br/>
                                        <Text type="secondary">{t(`payment-method-category-${category.toLowerCase()}-description`, {defaultValue: description})}</Text>
                                    </div>
                                    <Space wrap>
                                        {paymentMethodToggles.map(toggle => (
                                            <Space key={toggle.key}>
                                                <Switch
                                                    checked={paymentMethodState[category][toggle.key]}
                                                    disabled={!eventNumericId || eventPaymentMethodsQuery.loading || savingEventPaymentMethods}
                                                    aria-label={`${title} ${toggle.ariaSuffix}`}
                                                    onChange={checked => handlePaymentMethodToggle(category, toggle.key, checked)}
                                                />
                                                <Text>{toggle.label}</Text>
                                            </Space>
                                        ))}
                                    </Space>
                                </div>
                            </div>
                        </Col>
                    ))}
                </Row>
            </div>
        </Panel>
    );
};
